/**
 * I pulsanti delle viste, uguali ovunque (`docs/navigazione-e-viste.md`,
 * §1.13): in testata al massimo un pulsante pieno e le altre azioni nel menu
 * «⋯»; creare è «+ Nuovo …»; sulle righe un'azione sola è un'icona e più
 * azioni un menu; l'invio dei form gira la rotella finché lavora.
 */

import { type ComponentProps, type ReactNode, useContext } from 'react';
import type { LucideIcon } from 'lucide-react';
import { EllipsisIcon, PlusIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Spinner } from '@/components/ui/spinner';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { InTestata } from './inTestata.js';

/** Le azioni a destra della testata: il pulsante principale e il menu «⋯». */
export function AzioniTestata({ children }: { children: ReactNode }) {
  return <div className="flex shrink-0 items-center gap-2">{children}</div>;
}

/**
 * Il pulsante pieno della testata: icona e nome. Su telefono resta la sola
 * icona, con il nome nell'etichetta accessibile e nel titolo, perche le
 * azioni stiano sulla riga del titolo. Fuori dalla testata (stati vuoti,
 * schede) il nome resta sempre. Con `a` è un collegamento.
 */
export function PulsantePrincipale({
  etichetta,
  Icona,
  onClick,
  a,
  disabled,
  className,
}: {
  etichetta: string;
  Icona: LucideIcon;
  onClick?: () => void;
  a?: string;
  disabled?: boolean;
  className?: string;
}) {
  const compatto = useContext(InTestata);
  const contenuto = (
    <>
      <Icona data-icon="inline-start" />
      <span className={cn(compatto && 'hidden @xl:inline')}>{etichetta}</span>
    </>
  );
  const classi = cn(
    // Il padding dell'icona iniziale del Button vince su px-0: serve l'important.
    compatto && '@max-xl:w-8 @max-xl:px-0!',
    className,
  );
  if (a && !disabled) {
    return (
      <Button asChild className={classi}>
        <Link to={a} aria-label={etichetta} title={etichetta}>
          {contenuto}
        </Link>
      </Button>
    );
  }
  return (
    <Button
      className={classi}
      disabled={disabled}
      onClick={onClick}
      aria-label={etichetta}
      title={etichetta}
    >
      {contenuto}
    </Button>
  );
}

/**
 * Il pulsante che crea o aggiunge: pieno, con il «+» e il nome della cosa
 * («Nuova partita», «Aggiungi evento»); su telefono il solo «+».
 */
export function PulsanteNuovo(props: {
  etichetta: string;
  onClick?: () => void;
  a?: string;
  disabled?: boolean;
  className?: string;
}) {
  return <PulsantePrincipale Icona={PlusIcon} {...props} />;
}

/** Una voce del menu «⋯»; i valori falsi si saltano, per le voci condizionali. */
export type VoceAzione =
  | {
      etichetta: string;
      Icona: LucideIcon;
      onSelect?: () => void;
      /** Collegamento al posto di `onSelect`. */
      a?: string;
      disabled?: boolean;
      /** Sotto la voce ferma: perché non si può. */
      motivo?: string;
      /** Toglie o non si annulla: in rosso, in fondo, dopo una riga. */
      distruttiva?: boolean;
    }
  | false
  | null
  | undefined
  | ''
  | 0;

/**
 * Il menu «⋯» delle azioni che non sono la principale. In testata è
 * `outline`, accanto al pulsante pieno; su una riga è `ghost`, piccolo.
 * Senza voci non compare.
 */
export function MenuAzioni({
  voci,
  etichetta = 'Altre azioni',
  riga = false,
}: {
  voci: VoceAzione[];
  etichetta?: string;
  riga?: boolean;
}) {
  const valide = voci.filter((v) => !!v);
  if (valide.length === 0) return null;
  const normali = valide.filter((v) => !v.distruttiva);
  const distruttive = valide.filter((v) => v.distruttiva);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={riga ? 'ghost' : 'outline'}
          size={riga ? 'icon-sm' : 'icon'}
          aria-label={etichetta}
          title={etichetta}
          className="relative shrink-0"
        >
          <EllipsisIcon />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-auto min-w-44">
        {normali.map((v) => (
          <Voce key={v.etichetta} voce={v} />
        ))}
        {normali.length > 0 && distruttive.length > 0 && <DropdownMenuSeparator />}
        {distruttive.map((v) => (
          <Voce key={v.etichetta} voce={v} />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Voce({ voce }: { voce: Exclude<VoceAzione, false | null | undefined | '' | 0> }) {
  const { etichetta, Icona, onSelect, a, disabled, motivo, distruttiva } = voce;
  const contenuto = (
    <>
      <Icona />
      <span className="flex flex-col">
        {etichetta}
        {disabled && motivo && <span className="text-xs text-muted-foreground">{motivo}</span>}
      </span>
    </>
  );
  const variant = distruttiva ? 'destructive' : 'default';
  if (a && !disabled) {
    return (
      <DropdownMenuItem asChild variant={variant}>
        <Link to={a}>{contenuto}</Link>
      </DropdownMenuItem>
    );
  }
  return (
    <DropdownMenuItem variant={variant} disabled={disabled} onSelect={onSelect}>
      {contenuto}
    </DropdownMenuItem>
  );
}

/**
 * Un comando con la sola icona: il nome sta nel tooltip e nell'etichetta
 * accessibile. Il tooltip sta su uno span, così compare anche sul comando
 * disabilitato, con il `motivo` quando c'è. Con `a` è un collegamento. Sulle
 * righe degli elenchi è l'azione unica della riga, `ghost` e piccolo.
 */
export function ComandoIcona({
  etichetta,
  Icona,
  onClick,
  a,
  disabled,
  motivo,
  variant = 'ghost',
  size = 'icon',
  className,
}: {
  etichetta: string;
  Icona: LucideIcon;
  onClick?: () => void;
  a?: string;
  disabled?: boolean;
  motivo?: string;
  variant?: 'ghost' | 'outline' | 'default';
  size?: 'icon' | 'icon-sm';
  className?: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={className ?? 'inline-flex'}>
          {a && !disabled ? (
            <Button asChild variant={variant} size={size}>
              <Link to={a} aria-label={etichetta}>
                <Icona />
              </Link>
            </Button>
          ) : (
            <Button
              type="button"
              variant={variant}
              size={size}
              aria-label={etichetta}
              disabled={disabled}
              onClick={onClick}
            >
              <Icona />
            </Button>
          )}
        </span>
      </TooltipTrigger>
      <TooltipContent>{motivo ?? etichetta}</TooltipContent>
    </Tooltip>
  );
}

/**
 * Il pulsante che invia un form o conferma un dialog: l'etichetta resta la
 * stessa («Salva», «Crea partita») e, finché lavora, la rotella prende il
 * posto dell'icona e il pulsante si ferma.
 */
export function PulsanteInvio({
  inCorso = false,
  disabled,
  children,
  type = 'submit',
  className,
  ...resto
}: Omit<ComponentProps<typeof Button>, 'asChild'> & { inCorso?: boolean }) {
  return (
    <Button
      type={type}
      disabled={inCorso || disabled}
      className={cn(
        inCorso && '[&>[data-icon=inline-start]:not([data-slot=spinner])]:hidden',
        className,
      )}
      {...resto}
    >
      {inCorso && <Spinner data-icon="inline-start" />}
      {children}
    </Button>
  );
}
